import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Clock, MapPin, PoundSterling, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { supabase } from "../integrations/supabase/client";
import Session from "./Session";

const PodDetailDemo = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [podStatus, setPodStatus] = useState<{ in_use: boolean } | null>(null);
  const [showCards, setShowCards] = useState(false);

  const { data: pod, isLoading, error } = useQuery({
    queryKey: ['pod', id],
    queryFn: async () => {
      if (!id) throw new Error('Pod ID is required');
      
      const { data, error } = await supabase
        .from('pods')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Set up real-time subscription to listen for pod status changes
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel('pod-status-changes-demo')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pods'
        },
        (payload) => {
          console.log('Pod status change received:', payload);
          if (payload.new && payload.new.id === id) {
            setPodStatus({ in_use: payload.new.in_use });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  if (error) {
    console.error('Error fetching pod:', error);
  }

  const formatPrice = (pricePerMin: string) => {
    const price = parseFloat(pricePerMin);
    const price30Min = (price * 30).toFixed(2);
    return {
      perMin: `£${price}/min`,
      per30Min: `£${price30Min}/30min`
    };
  };

  const handleUseNow = () => {
    setShowCards(true);
  };

  // Show Session flow
  if (showCards) {
    return <Session />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f9f8f5]">
        <Header />
        <main className="pt-20">
          <div className="max-w-[800px] mx-auto px-4 desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6 py-12">
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#374151]"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!pod) {
    return (
      <div className="min-h-screen bg-[#f9f8f5]">
        <Header />
        <main className="pt-20">
          <div className="max-w-[800px] mx-auto px-4 desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6 py-12">
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">Pod not found</div>
              <button
                onClick={() => navigate('/pods')}
                className="mt-4 text-[#374151] hover:underline flex items-center gap-2 mx-auto"
              >
                <ArrowLeft size={16} />
                Back to Pods
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pricing = formatPrice(pod.price.toString());
  const currentInUse = podStatus?.in_use ?? pod.in_use;

  return (
    <div className="min-h-screen bg-[#f9f8f5]">
      <Header />
      
      <main className="pt-20">
        <div className="max-w-[800px] mx-auto px-4 desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button
              onClick={() => navigate('/pods')}
              className="flex items-center gap-2 text-[#374151] hover:text-[#374151]/80 mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Pods
            </button>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mb-8"
              >
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl desktop:text-4xl laptop:text-3xl tablet:text-2xl mobile:text-xl font-bold text-[#374151]">
                    {pod.name}
                  </h1>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    currentInUse 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {currentInUse ? 'In Use' : 'Available'}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6 mb-8"
              >
                <div className="flex items-start gap-4">
                  <MapPin className="text-[#374151] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#374151] mb-1">Location</h3>
                    <p className="text-gray-700 text-lg">{pod.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="text-[#374151] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#374151] mb-1">Opening Hours</h3>
                    <p className="text-gray-700 text-lg">{pod.opening_times}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <PoundSterling className="text-[#374151] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#374151] mb-1">Pricing</h3>
                    <div className="text-gray-700 text-lg">
                      <span className="font-medium text-xl">{pricing.perMin}</span>
                      <span className="text-gray-500 ml-3">({pricing.per30Min})</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-[#374151] mt-1 flex-shrink-0">
                    <div className={`w-6 h-6 rounded-full ${currentInUse ? 'bg-red-500' : 'bg-green-500'}`}></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#374151] mb-1">Status</h3>
                    <p className="text-gray-700 text-lg">
                      {currentInUse ? 'Currently occupied' : 'Ready to use'}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="pt-6 border-t border-gray-200"
              >
                <button
                  onClick={handleUseNow}
                  disabled={currentInUse}
                  className={`w-full py-4 px-8 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 transition-colors ${
                    currentInUse
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#374151] text-white hover:bg-[#374151]/90'
                  }`}
                >
                  {currentInUse ? 'Pod In Use' : 'Use Now'}
                </button>
                <p className="text-center text-gray-500 text-sm mt-2">
                  {currentInUse ? 'This pod is currently occupied' : 'Start your session immediately'}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PodDetailDemo;
